import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];
const IMG = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Consulting = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.services)).catch(() => {});
  }, []);

  return (
    <section id="consulting" className="relative py-28 lg:py-36" data-testid="consulting-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-5"
            >
              Chapter 02 / Enterprise Consulting
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight"
            >
              Transforming the Office<br />of the CFO.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              className="mt-6 max-w-xl text-sm sm:text-base text-slate-600 leading-relaxed"
            >
              Business-led SAP transformation across Finance, Revenue, Treasury, Reporting, Compliance, and Planning — from greenfield builds to global rollouts.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -inset-2 rounded-3xl bg-gradient-to-tr from-sky-500/15 to-amber-500/15 blur-lg" />
            <img
              src={IMG}
              alt="SAP consulting strategy session"
              className="relative rounded-3xl border border-slate-200 w-full aspect-[16/9] object-cover"
              data-testid="consulting-image"
            />
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.08 }}
              className="group relative rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7 flex flex-col hover:-translate-y-1.5 hover:border-sky-400/40 hover:shadow-[0_20px_60px_-20px_rgba(56,189,248,0.2)] transition-all duration-500"
              data-testid={`consulting-card-${s.id}`}
            >
              <span className="self-start font-mono2 text-[9px] uppercase tracking-[0.2em] text-sky-600 border border-sky-400/30 bg-sky-400/10 px-3 py-1 rounded-full mb-5">
                {s.tag}
              </span>
              <Link to={`/consulting/${s.id}`} data-testid={`consulting-link-${s.id}`}>
                <h3 className="font-display text-xl font-bold tracking-tight leading-snug mb-3 group-hover:text-amber-700 transition-colors duration-300">{s.title}</h3>
              </Link>
              <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">{s.description}</p>
              <ul className="space-y-1.5 mb-6">
                {s.deliverables.map((d) => (
                  <li key={d} className="font-mono2 text-[11px] text-slate-500 flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-amber-400" /> {d}
                  </li>
                ))}
              </ul>
              <Link
                to={`/consulting/${s.id}`}
                className="self-start flex items-center gap-1.5 text-xs font-semibold text-slate-700 group-hover:text-amber-700 transition-colors duration-300"
                data-testid={`consulting-inquire-${s.id}`}
              >
                View service details <ArrowUpRight size={13} />
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Consulting;
